'use client';

import Image, { type ImageProps } from 'next/image';
import React from 'react';

type LoaderOverlayProps = {
  roundedClassName?: string;
};

function LoaderOverlay({ roundedClassName = '' }: LoaderOverlayProps) {
  return (
    <div
      className={`pointer-events-none absolute inset-0 z-10 flex items-center justify-center overflow-hidden bg-[#0b0d19]/70 ${roundedClassName}`}
      aria-hidden="true"
    >
      <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-white/5 via-white/10 to-white/5" />
      <div className="relative h-7 w-7 rounded-full border-2 border-white/20 border-t-[#ffb74d] motion-safe:animate-spin" />
    </div>
  );
}

type NativeImageWithLoaderProps = Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'className' | 'onLoad'> & {
  imageClassName?: string;
  wrapperClassName?: string;
  loaderRoundedClassName?: string;
  onLoad?: React.ReactEventHandler<HTMLImageElement>;
};

export function NativeImageWithLoader({
  imageClassName = '',
  wrapperClassName = '',
  loaderRoundedClassName,
  src,
  onLoad,
  onError,
  ...props
}: NativeImageWithLoaderProps) {
  const [loaded, setLoaded] = React.useState(false);
  const normalizedSrc = typeof src === 'string' ? src : '';

  React.useEffect(() => {
    setLoaded(false);
  }, [normalizedSrc]);

  return (
    <span className={`relative block overflow-hidden ${wrapperClassName}`}>
      {!loaded ? <LoaderOverlay roundedClassName={loaderRoundedClassName} /> : null}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        {...props}
        src={src}
        className={`${imageClassName} transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
        onLoad={(event) => {
          setLoaded(true);
          onLoad?.(event);
        }}
        onError={(event) => {
          setLoaded(true);
          onError?.(event);
        }}
      />
    </span>
  );
}

type NextImageWithLoaderProps = ImageProps & {
  wrapperClassName?: string;
  loaderRoundedClassName?: string;
};

export function NextImageWithLoader({
  className = '',
  wrapperClassName = '',
  loaderRoundedClassName,
  src,
  onLoad,
  onError,
  ...props
}: NextImageWithLoaderProps) {
  const [loaded, setLoaded] = React.useState(false);

  React.useEffect(() => {
    setLoaded(false);
  }, [src]);

  return (
    <span className={`relative block overflow-hidden ${wrapperClassName}`}>
      {!loaded ? <LoaderOverlay roundedClassName={loaderRoundedClassName} /> : null}
      <Image
        {...props}
        src={src}
        className={`${className} transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
        onLoad={(event) => {
          setLoaded(true);
          onLoad?.(event);
        }}
        onError={(event) => {
          setLoaded(true);
          onError?.(event);
        }}
      />
    </span>
  );
}
